import { CustomButton } from '@/components/buttons/Buttons';
import { useNavigate, useParams } from 'react-router-dom';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { useRef, useState } from 'react';
import { MdArrowBack, MdMoreVert } from 'react-icons/md';
import EditGroupDialog from './EditGroupDialog';
import ListGroupPeopleScreen from './ListGroupPeopleScreen';
import './people.styles.css';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { GenericResponse } from '@/common/common_utils';
import { TextHeadlineExtraExtraLarge2, TextLabelLarge2, TextTitleMedium2 } from '@/components/text/Texts';
import { Column, Row } from '@/components/container/Container';
import { Button } from 'primereact/button';
import StyledTabView from '@/components/tabs/StyledTabView';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

interface GetGroupRequest {
	Id: number;
}

export interface GetGroupResponse {
	Id: number;
	Name: string;
	Description: string;
}

const GroupInfoScreen = () => {
	const { id } = useParams<{ id: string }>();
	const groupId = Number(id);
	if (!id || isNaN(groupId)) throw new Error("Invalid group id");

	const navigate = useNavigate();
	const [openEditModal, setOpenEditModal] = useState(false);
	const menu = useRef<Menu>(null);
	const isManagerOrAdmin = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.Admin, X4bUserRoleEnum.Manager]);

	const { data: group } = useQuery({
		queryKey: ['GroupInfoScreen.employee', groupId],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<GetGroupResponse>>(
				'/api/X4bGroup/GetGroup',
				{ Id: groupId } as GetGroupRequest
			);
			return result.data.Data;
		},
	});

	const menuItems: MenuItem[] = [{ label: 'Edit Group', command: () => setOpenEditModal(true) }];

	return (
		<Column className="gap-3 h-full">
			<Button
				text
				className="self-start pl-3 pr-4 py-2 gap-2 bg-transparent hover:underline focus:outline-none"
				onClick={() => navigate(-1)}
			>
				<MdArrowBack size={18} className="text-primary60" />
				<TextLabelLarge2 text="Back" className="text-primary60" />
			</Button>

			<Row className="gap-2.5 items-end">
				<Column className="w-full my-2">
					<Row className="gap-2.5">
						<TextHeadlineExtraExtraLarge2 text={group?.Name ?? ''} className="text-primary60" />
						{isManagerOrAdmin &&
							<CustomButton
								id="edit-group"
								aria-controls="edit-group"
								aria-haspopup="true"
								rounded
								className="p-3 hover:bg-surfaceContainerHighest"
								onClick={(event) => menu.current?.toggle(event)}
							>
								<MdMoreVert size={24} className="text-primary" />
							</CustomButton>}
						<Menu
							model={menuItems}
							popup
							ref={menu}
							id="edit-group"
							pt={{
								root: { className: "bg-surface font-poppins text-body-large w-48" },
								content: { className: "bg-surface font-poppins text-body-large w-48" },
							}}
						/>
					</Row>
					<TextTitleMedium2 text={group?.Description ?? ''} className="text-neutralVariant20" />
				</Column>

				<StyledTabView tabs={[{ header: 'People' }, { header: 'Programs' }]} />
			</Row>

			{/* TODO handle loading */}
			{group && (<ListGroupPeopleScreen group={group} />)}

			{/* TODO handle loading */}
			{group && (
				<EditGroupDialog
					visible={openEditModal}
					onHide={() => setOpenEditModal(false)}
					group={group}
				/>
			)}
		</Column>
	);
};

export default GroupInfoScreen;
